import { buildStepContent } from '../block-builder';
import { insertHost, keepHostAttached, readInlinePosition } from '../inline-host';
import type { GuideInteractionMetadata, GuideRenderContext, GuideStep } from '../types';
import { waitForElement } from '../wait-for-element';
import { BaseRenderer } from './base-renderer';

/**
 * Renderiza una guía `inline`: EMBEBIDA en el flujo del DOM, junto a un
 * elemento ancla (no flota como overlay). A diferencia de modal/banner/tooltip
 * (host fijo/absoluto sobre `body`), aquí el host se mueve al flujo del
 * documento — ocupa su propio espacio en el layout.
 *
 * `step.selector` = elemento ancla; `step.style.inlinePosition`
 * (`before`|`after`|`prepend`|`append`) = dónde insertarse. Single-step.
 */
export class InlineRenderer extends BaseRenderer {
  async render(context: GuideRenderContext): Promise<void> {
    const step = context.guide.guideSteps[0];
    if (!step) return;

    const selector = step.selector ?? context.guide.activationRules.selector;
    if (typeof selector !== 'string' || selector.length === 0) return;

    const anchor = await waitForElement(selector);
    if (!anchor) return;

    const { host, root } = this.createHost();
    // createHost adjuntó el host a <body>; lo movemos al flujo del DOM, junto
    // al ancla. `display:block` vence el `:host{all:initial}` (que sería inline).
    host.style.display = 'block';
    const position = readInlinePosition(step.style);
    insertHost(anchor, host, position);
    // El framework del host puede borrar el nodo al re-renderizar → watchdog.
    this.registerCleanup(keepHostAttached(host, selector, position));

    this.applyDesign(step.style);
    const ownerDocument = root.ownerDocument ?? document;

    const card = ownerDocument.createElement('div');
    card.className = 'veo-inline';

    const dismiss = (
      action: 'dismissed' | 'cta_clicked',
      ctaUrl?: string,
      meta?: GuideInteractionMetadata,
    ) => {
      context.onInteraction({
        guideId: context.guide.guideId,
        stepIndex: 0,
        action,
        ...(meta ? { metadata: meta } : {}),
      });
      if (ctaUrl) window.open(ctaUrl, '_blank', 'noopener,noreferrer');
      context.onClose();
    };

    const content = buildStepContent(step, ownerDocument, {
      onCtaClick: (action, target, meta) => {
        dismiss('cta_clicked', action === 'url' ? target : undefined, meta);
        if (action === 'guide' && target) context.onOpenGuide?.(target);
      },
      onDismiss: () => dismiss('dismissed'),
    });

    card.appendChild(content);
    root.appendChild(card);

    // Update in-place (preview): misma ancla/posición → solo cambia contenido.
    this.liveContainer = card;
    this.liveContent = content;
    this.liveKey = `${selector}|${position}`;
    this.liveBuild = (s) =>
      buildStepContent(s, ownerDocument, {
        onCtaClick: (action, target, meta) => {
          dismiss('cta_clicked', action === 'url' ? target : undefined, meta);
          if (action === 'guide' && target) context.onOpenGuide?.(target);
        },
        onDismiss: () => dismiss('dismissed'),
      });

    context.onInteraction({ guideId: context.guide.guideId, stepIndex: 0, action: 'shown' });
  }

  protected override liveKeyOf(step: GuideStep): string {
    return `${step.selector ?? ''}|${readInlinePosition(step.style)}`;
  }
}
